import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Eventcalendar,
	MbscCalendarEvent,
	MbscCalendarEventData,
	MbscEventcalendarView,
	momentTimezone,
	setOptions,
} from '@mobiscroll/react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
} from '@mui/material';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { deleteEvent, fetchAllEvents } from 'core/API/events';
import { fetchHolidayCalendar } from 'core/API/holidayCalendar';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import Loading from 'core/components/Loading/Loading';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { buildDateTimeStr, mapApiEvents } from '../utils/eventsCalendarUtils';

import './EventsMonth.style.scss';

momentTimezone.moment = momentTZ;
setOptions({
	theme: 'ios',
	themeVariant: 'light',
});

interface EventsMonthProps {
	showBackButton?: boolean;
}

const EventsMonth = ({ showBackButton = true }: EventsMonthProps): JSX.Element => {
	const { workplaceId, timezones: ianaTimezone } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const history = useHistory();
	const queryClient = useQueryClient();

	const {
		hasModuleViewPermission,
		hasModuleEditPermission,
		hasModuleDeletePermission,
		hasModuleExecutePermission,
	} = usePermissionCheck();

	const canViewEvents = hasModuleViewPermission('Events');
	const canEditEvents = hasModuleEditPermission('Events');
	const canDeleteEvents = hasModuleDeletePermission('Events');
	const canCreateEvents = canEditEvents || hasModuleExecutePermission('Events');

	const [appliedMonth, setAppliedMonth] = useState(() => {
		const storedDate = localStorage.getItem('events-selected-date');
		const base =
			storedDate && moment(storedDate).isValid() ? moment(storedDate) : moment().startOf('month');
		return base.startOf('month').toDate();
	});
	const [appliedView] = useState<'month'>('month');
	const [pendingMonth, setPendingMonth] = useState(() => {
		const storedDate = localStorage.getItem('events-selected-date');
		const base =
			storedDate && moment(storedDate).isValid() ? moment(storedDate) : moment().startOf('month');
		return base.startOf('month').toDate();
	});
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');
	const [isApplying, setIsApplying] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [eventToDelete, setEventToDelete] = useState<MbscCalendarEvent | null>(null);
	const [dayPickerDialog, setDayPickerDialog] = useState<{
		open: boolean;
		date: string;
		events: MbscCalendarEvent[];
	}>({ open: false, date: '', events: [] });
	const months = useMemo(() => moment.months(), []);
	useEffect(() => {
		const handleDateChange = (event: CustomEvent) => {
			const rawDate = event.detail;
			const nextMoment = moment(rawDate).isValid()
				? moment(rawDate).startOf('month')
				: moment().startOf('month');
			setPendingMonth(nextMoment.toDate());
			setAppliedMonth(nextMoment.toDate());
			setIsApplying(true);
		};

		window.addEventListener('events-date-changed', handleDateChange as EventListener);
		return () => {
			window.removeEventListener('events-date-changed', handleDateChange as EventListener);
		};
	}, []);

	// Sync applied month from localStorage on mount so we match the header after navigation (e.g. back from create/edit)
	useEffect(() => {
		const stored = localStorage.getItem('events-selected-date');
		if (!stored || !moment(stored).isValid()) return;
		const storedMonth = moment(stored).startOf('month');
		setPendingMonth(storedMonth.toDate());
		setAppliedMonth(storedMonth.toDate());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const view = useMemo<MbscEventcalendarView>(() => {
		return {
			calendar: {
				type: appliedView,
				labelsDisplay: 'inline' as const,
				maxEventStack: 8,
			},
		};
	}, [appliedView]);

	useEffect(() => {
		const base = moment(appliedMonth);
		const rangeStart = base.startOf('month');
		const rangeEnd = base.clone().add(1, 'month').startOf('month');
		setFromDate(rangeStart.format('YYYY-MM-DD'));
		setToDate(rangeEnd.format('YYYY-MM-DD'));
	}, [appliedMonth, appliedView]);

	const {
		data: monthEvents = [],
		isLoading,
		isFetching,
	} = useQuery(
		['events-month', workplaceId, fromDate, toDate],
		() =>
			fetchAllEvents({
				workplaceId,
				fromDate,
				toDate,
				pagination: false,
			}),
		{
			enabled: !!workplaceId && !!fromDate && !!toDate,
			refetchInterval: 600000,
			keepPreviousData: true,
			select: (data) => {
				const items = data?.member || [];
				return mapApiEvents(items);
			},
		}
	);

	const holidayFromDate = useMemo(
		() => moment(appliedMonth).startOf('month').format('YYYY-MM-DD'),
		[appliedMonth]
	);
	const holidayToDate = useMemo(
		() => moment(appliedMonth).endOf('month').format('YYYY-MM-DD'),
		[appliedMonth]
	);
	const { data: holidayItems = [] } = useQuery(
		['holiday-calendar', holidayFromDate, holidayToDate],
		() => fetchHolidayCalendar({ from_date: holidayFromDate, to_date: holidayToDate }),
		{
			enabled: !!holidayFromDate && !!holidayToDate,
			refetchInterval: 600000,
			keepPreviousData: true,
		}
	);
	const holidayEvents = useMemo((): MbscCalendarEvent[] => {
		return holidayItems
			.filter((item) => item.holiday_date)
			.map((item, index) => {
				const dateStr = item.holiday_date!;
				const start = moment(dateStr).startOf('day').toDate();
				const end = moment(dateStr).endOf('day').toDate();
				const title = item.name || item.title || 'Off day';
				return {
					id: `holiday-${dateStr}-${item.id ?? index}`,
					start,
					end,
					title,
					name: title,
					originalEvent: { isHoliday: true, ...item },
					background_color: 'rgb(120, 120, 120)',
					text_color: 'white',
					allDay: true,
				} as MbscCalendarEvent;
			});
	}, [holidayItems]);

	const { mutate: deleteEventMutation, isLoading: isDeletingEvent } = useMutation(deleteEvent, {
		onSuccess: async () => {
			showToast('success', 'Event successfully deleted!');
			queryClient.invalidateQueries(['events-month']);
			await queryClient.refetchQueries(['events-month', workplaceId, fromDate, toDate]);
			setIsDeleteDialogOpen(false);
			setEventToDelete(null);
		},
		onError: () => {
			showToast('error', 'Failed to delete event');
			setIsDeleteDialogOpen(false);
			setEventToDelete(null);
		},
	});

	useEffect(() => {
		if (!isFetching && isApplying) {
			setIsApplying(false);
		}
	}, [isFetching, isApplying]);

	const handleAddEvent = useCallback(
		(payload?: any) => {
			if (!canCreateEvents) {
				showToast('error', 'You do not have permission to create events');
				return;
			}
			const state: any = {};
			if (payload?.date) {
				state.date = new Date(payload.date);
				state.startTime = '09:00';
				state.endTime = '10:00';
			}
			history.push({
				pathname: ROUTES.CREATE_EVENT,
				state,
			});
		},
		[canCreateEvents, history, showToast]
	);

	const handleEditEvent = useCallback(
		(payload: any) => {
			if (!canEditEvents) {
				showToast('error', 'You do not have permission to edit events');
				return;
			}
			const actualEventId = payload?.originalEvent?.id || payload?.id;
			const eventData = payload?.originalEvent || payload;

			if (eventData?.startDate || eventData?.start) {
				const eventDate = moment(eventData?.startDate || eventData?.start).format('YYYY-MM-DD');
				localStorage.setItem('events-selected-date', eventDate);
			}

			history.push({
				pathname: ROUTES.CREATE_EVENT,
				state: {
					editEvent: {
						...eventData,
						startDate: eventData?.startDate || eventData?.start || null,
						endDate: eventData?.endDate || eventData?.end || null,
						startTime: eventData?.startTime || null,
						endTime: eventData?.endTime || null,
					},
					eventId: actualEventId,
				},
			});
		},
		[canEditEvents, history, showToast]
	);

	const handleViewEvent = useCallback(
		(payload: any) => {
			if (!canViewEvents) {
				showToast('error', 'You do not have permission to view events');
				return;
			}
			const actualEventId = payload?.originalEvent?.id || payload?.id;
			const eventData = payload?.originalEvent || payload;

			if (eventData?.startDate || eventData?.start) {
				const eventDate = moment(eventData?.startDate || eventData?.start).format('YYYY-MM-DD');
				localStorage.setItem('events-selected-date', eventDate);
			}

			history.push({
				pathname: ROUTES.EVENT_DETAILS,
				state: { event: eventData, eventId: actualEventId },
			});
		},
		[canViewEvents, history, showToast]
	);

	const handleDeleteEvent = useCallback(
		(event: MbscCalendarEventData['original']) => {
			if (!canDeleteEvents) {
				showToast('error', 'You do not have permission to delete events');
				return;
			}
			const eventId = (event as any)?.originalEvent?.id || (event as any)?.id;
			if (!eventId) return;
			setEventToDelete(event as MbscCalendarEvent);
			setIsDeleteDialogOpen(true);
		},
		[canDeleteEvents, showToast]
	);

	const handleConfirmDelete = useCallback(() => {
		const eventId = eventToDelete?.originalEvent?.id || eventToDelete?.id;
		if (!eventId) return;
		deleteEventMutation(Number(eventId));
	}, [deleteEventMutation, eventToDelete]);

	const {
		eventCountsByDate,
		firstEventIdByDate,
		dateKeyByEventId,
		groupedEvents: _groupedEvents,
		calendarDisplayEvents,
	} = useMemo(() => {
		const counts = new Map<string, number>();
		const firstEventId = new Map<string, string>();
		const earliestTimeByDate = new Map<string, number>();
		const dateKeyById = new Map<string, string>();
		const grouped = new Map<string, MbscCalendarEvent[]>();
		const firstEventByDate = new Map<string, MbscCalendarEvent>();

		monthEvents.forEach((event) => {
			const eventStart =
				(event as MbscCalendarEvent).start ||
				(event as any)?.originalEvent?.startDate ||
				(event as any)?.originalEvent?.start;
			if (!eventStart) return;
			const key = moment(eventStart).format('YYYY-MM-DD');
			const eventId = String((event as MbscCalendarEvent).id);
			const eventStartTime = moment(eventStart).valueOf();

			dateKeyById.set(eventId, key);
			counts.set(key, (counts.get(key) || 0) + 1);

			const currentEarliest = earliestTimeByDate.get(key);
			if (currentEarliest === undefined || eventStartTime < currentEarliest) {
				earliestTimeByDate.set(key, eventStartTime);
				firstEventId.set(key, eventId);
				firstEventByDate.set(key, event as MbscCalendarEvent);
			}

			if (!grouped.has(key)) {
				grouped.set(key, []);
			}
			grouped.get(key)!.push(event as MbscCalendarEvent);
		});

		const groupedList = Array.from(grouped.entries())
			.sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
			.map(([date, items]) => ({
				date,
				items: items.sort((a, b) => {
					const aTime = a.start ? moment(a.start).valueOf() : 0;
					const bTime = b.start ? moment(b.start).valueOf() : 0;
					return aTime - bTime;
				}),
			}));

		// One event per day for calendar grid so we only show "Total Events: N" pill (no "N more" plain text)
		const displayEvents = Array.from(firstEventByDate.values());

		return {
			eventCountsByDate: counts,
			firstEventIdByDate: firstEventId,
			dateKeyByEventId: dateKeyById,
			groupedEvents: groupedList,
			calendarDisplayEvents: displayEvents,
		};
	}, [monthEvents]);

	const calendarDisplayEventsWithHolidays = useMemo(
		() => [...calendarDisplayEvents, ...holidayEvents],
		[calendarDisplayEvents, holidayEvents]
	);

	const groupedListEvents = useMemo(() => {
		const selectedStart = moment(appliedMonth).startOf('month');
		const selectedEnd = moment(appliedMonth).endOf('month');

		const grouped = new Map<string, MbscCalendarEvent[]>();
		monthEvents.forEach((event) => {
			const eventStart =
				(event as MbscCalendarEvent).start ||
				(event as any)?.originalEvent?.startDate ||
				(event as any)?.originalEvent?.start;
			if (!eventStart) return;
			// Only include events within the displayed month
			if (!moment(eventStart).isBetween(selectedStart, selectedEnd, 'day', '[]')) return;
			const key = moment(eventStart).format('YYYY-MM-DD');
			if (!grouped.has(key)) {
				grouped.set(key, []);
			}
			grouped.get(key)!.push(event as MbscCalendarEvent);
		});

		return Array.from(grouped.entries())
			.sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
			.map(([date, items]) => ({
				date,
				items: items.sort((a, b) => {
					const aTime = a.start ? moment(a.start).valueOf() : 0;
					const bTime = b.start ? moment(b.start).valueOf() : 0;
					return aTime - bTime;
				}),
			}));
	}, [monthEvents, appliedMonth]);

	const renderEventContent = useCallback(
		(data: MbscCalendarEventData) => {
			const original = data.original as MbscCalendarEvent;
			const isHoliday = (original as any)?.originalEvent?.isHoliday;
			if (isHoliday) {
				return (
					<div className="events-month-event events-month-event--holiday" title="Holiday">
						<span className="events-month-event__holiday-label">
							{original?.title || original?.name || 'Off day'}
						</span>
					</div>
				);
			}
			const eventId = String(original?.id ?? '');
			const key =
				dateKeyByEventId.get(eventId) ||
				(original?.start ? moment(original.start).format('YYYY-MM-DD') : '');
			if (!key) return null;
			const count = eventCountsByDate.get(key) || 0;
			if (count === 0) return null;
			const firstId = firstEventIdByDate.get(key);
			if (firstId && String(original?.id) !== firstId) return null;

			return (
				<div className="events-month-event events-month-event--booked">Total Events: {count}</div>
			);
		},
		[eventCountsByDate, dateKeyByEventId, firstEventIdByDate]
	);

	const renderHeader = () => {
		const currentMoment = moment(pendingMonth);
		const currentMonthIndex = currentMoment.month();
		const currentYear = currentMoment.year();

		return (
			<div className="events-month-header">
				<div className="events-month-header__left">
					<div className="events-month-header__date">
						<span className="events-month-header__month">{months[currentMonthIndex]}</span>
						<span className="events-month-header__year">{currentYear}</span>
					</div>
				</div>
				<div className="events-month-header__right">
					{showBackButton && (
						<button
							className="events-month-header__back"
							onClick={() => history.push(ROUTES.EVENTS)}
						>
							Back
						</button>
					)}
				</div>
			</div>
		);
	};

	if (!canViewEvents) {
		return (
			<div className="events-month-access">
				<div className="events-month-access__card">
					<h2>Access Denied</h2>
					<p>You do not have permission to view events.</p>
				</div>
			</div>
		);
	}

	return (
		<section className="events-month-page">
			<div className="events-month-layout">
				<div className="events-month-calendar-wrap">
					<Eventcalendar
						theme="ios"
						themeVariant="light"
						{...(ianaTimezone ? { dataTimezone: ianaTimezone, displayTimezone: ianaTimezone } : {})}
						timezonePlugin={momentTimezone}
						view={view}
						selectedDate={appliedMonth}
						data={calendarDisplayEventsWithHolidays}
						clickToCreate={false}
						dragToMove={false}
						dragToResize={false}
						renderHeader={renderHeader}
						renderEventContent={renderEventContent}
						{...({ renderEvent: renderEventContent } as any)}
						{...({ renderLabelContent: renderEventContent } as any)}
						{...({ renderLabel: renderEventContent } as any)}
						cssClass="events-month-calendar"
						onPageChange={(args) => {
							// firstDay is the first *visible* day in the grid, which can be from the
							// previous month (e.g. showing April shows March 29 as firstDay).
							// Adding 6 days always lands inside the actual displayed month.
							const newMonth = moment((args as any).firstDay)
								.add(6, 'days')
								.startOf('month')
								.toDate();
							const newMonthStr = moment(newMonth).format('YYYY-MM-DD');
							setPendingMonth(newMonth);
							setAppliedMonth(newMonth);
							setIsApplying(true);
							localStorage.setItem('events-selected-date', newMonthStr);
							window.dispatchEvent(
								new CustomEvent('events-calendar-page-changed', { detail: newMonthStr })
							);
						}}
						onCellClick={(args) => {
							// args.events is populated by Mobiscroll from its internal event map for the clicked cell.
							// Use the event's own .start (local Date) to derive the dateKey, instead of args.date
							// which is a UTC-midnight value that causes an off-by-one in negative-UTC-offset timezones.
							const cellEvents = ((args as any).events as MbscCalendarEvent[] | undefined) || [];
							const regularEvents = cellEvents.filter((e) => !(e as any)?.originalEvent?.isHoliday);
							if (regularEvents.length > 0) {
								const dateKey = moment((regularEvents[0] as any).start).format('YYYY-MM-DD');
								// Search all monthEvents (not just the appliedMonth-filtered groupedListEvents)
								// so that overflow days visible in the grid (e.g. March 29 in April's view)
								// also open the popup when they contain events.
								const allEventsForDay = (monthEvents as MbscCalendarEvent[])
									.filter((event) => {
										const eventStart =
											event.start ||
											(event as any)?.originalEvent?.startDate ||
											(event as any)?.originalEvent?.start;
										return eventStart && moment(eventStart).format('YYYY-MM-DD') === dateKey;
									})
									.sort((a, b) => {
										const aTime = a.start ? moment(a.start).valueOf() : 0;
										const bTime = b.start ? moment(b.start).valueOf() : 0;
										return aTime - bTime;
									});
								if (allEventsForDay.length > 0) {
									setDayPickerDialog({ open: true, date: dateKey, events: allEventsForDay });
									return;
								}
							}
							handleAddEvent(args);
						}}
					/>
					{(isLoading || isFetching || isApplying) && (
						<div className="events-month-loading">
							<Loading />
						</div>
					)}
				</div>
				<aside className="events-month-list">
					<div className="events-month-list__header">
						<span className="events-month-list__title">Booked Events</span>
						{canCreateEvents && (
							<button className="events-month-list__add" onClick={() => handleAddEvent()}>
								Add
							</button>
						)}
					</div>
					<div className="events-month-list__body">
						{groupedListEvents.length === 0 ? (
							<div className="events-month-list__empty">No events available.</div>
						) : (
							groupedListEvents.map(({ date, items }) => (
								<div key={date} className="events-month-list__section">
									<div className="events-month-list__date">
										{moment(date).format('dddd, MMMM D, YYYY')}
									</div>
									{items.map((event) => (
										<div key={String(event.id)} className="events-month-list__item">
											<div className="events-month-list__info">
												<div className="events-month-list__item-title">
													{event.name || event.title}
												</div>
												<div className="events-month-list__item-time">
													Start at {moment(event.start).format('h:mm A')}
												</div>
											</div>
											<div className="events-month-list__actions">
												{canViewEvents && (
													<button
														type="button"
														className="events-month-list__action"
														onClick={() => handleViewEvent(event)}
														title="View"
													>
														<VisibilityIcon fontSize="small" />
													</button>
												)}
												{canEditEvents && (
													<button
														type="button"
														className="events-month-list__action"
														onClick={() => handleEditEvent(event)}
														title="Edit"
													>
														<EditIcon fontSize="small" />
													</button>
												)}
												{canDeleteEvents && (
													<button
														type="button"
														className="events-month-list__action events-month-list__action--danger"
														onClick={() => handleDeleteEvent(event)}
														disabled={isDeletingEvent}
														title="Delete"
													>
														<DeleteIcon fontSize="small" />
													</button>
												)}
											</div>
										</div>
									))}
								</div>
							))
						)}
					</div>
				</aside>
			</div>
			{/* Day Events Picker Dialog */}
			<Dialog
				open={dayPickerDialog.open}
				onClose={() => setDayPickerDialog((s) => ({ ...s, open: false }))}
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
					},
				}}
			>
				<DialogTitle style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
					Events on{' '}
					{dayPickerDialog.date ? moment(dayPickerDialog.date).format('dddd, MMMM D, YYYY') : ''}
				</DialogTitle>
				<DialogContent style={{ padding: '16px 24px' }}>
					{canCreateEvents && (
						<Button
							fullWidth
							variant="outlined"
							style={{ marginBottom: '16px', borderStyle: 'dashed' }}
							onClick={() => {
								setDayPickerDialog((s) => ({ ...s, open: false }));
								handleAddEvent({ date: moment(dayPickerDialog.date).toDate() });
							}}
						>
							+ Create New Event
						</Button>
					)}
					<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{dayPickerDialog.events.map((ev) => (
							<div
								key={String(ev.id)}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									padding: '10px 14px',
									background: '#f8f9fa',
									borderRadius: '6px',
									border: '1px solid #e9ecef',
								}}
							>
								<div>
									<div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
										{(ev as any).name || ev.title}
									</div>
									<div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
										{ev.start ? moment(ev.start).format('h:mm A') : ''}
										{ev.end ? ` – ${moment(ev.end).format('h:mm A')}` : ''}
									</div>
								</div>
								<div style={{ display: 'flex', gap: '4px' }}>
									{canViewEvents && (
										<button
											type="button"
											className="events-month-list__action"
											title="View"
											onClick={() => {
												setDayPickerDialog((s) => ({ ...s, open: false }));
												handleViewEvent(ev);
											}}
										>
											<VisibilityIcon fontSize="small" />
										</button>
									)}
									{canEditEvents && (
										<button
											type="button"
											className="events-month-list__action"
											title="Edit"
											onClick={() => {
												setDayPickerDialog((s) => ({ ...s, open: false }));
												handleEditEvent(ev);
											}}
										>
											<EditIcon fontSize="small" />
										</button>
									)}
									{canDeleteEvents && (
										<button
											type="button"
											className="events-month-list__action events-month-list__action--danger"
											title="Delete"
											onClick={() => {
												setDayPickerDialog((s) => ({ ...s, open: false }));
												handleDeleteEvent(ev);
											}}
										>
											<DeleteIcon fontSize="small" />
										</button>
									)}
								</div>
							</div>
						))}
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDayPickerDialog((s) => ({ ...s, open: false }))}
						style={{ color: 'black' }}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={isDeleteDialogOpen}
				onClose={() => {
					setIsDeleteDialogOpen(false);
					setEventToDelete(null);
				}}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px',
					},
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the event{' '}
						{eventToDelete && (
							<strong>
								&ldquo;{eventToDelete.name || eventToDelete.title || 'this event'}&rdquo;
							</strong>
						)}
						?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setIsDeleteDialogOpen(false);
							setEventToDelete(null);
						}}
						style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
						disabled={isDeletingEvent}
					>
						Cancel
					</Button>
					<Button
						style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
						onClick={handleConfirmDelete}
						disabled={isDeletingEvent}
					>
						{isDeletingEvent ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>
		</section>
	);
};

export default EventsMonth;
